'use client';

import { c as _c } from "react/compiler-runtime";
import { convictionFraction, useGetMarketField, useMarketConfig } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import { InteractiveExplainerButton } from '@/components/how-it-works/interactive-explainer-button';
import { buildOutcomeRowSummaries } from '@/components/markets/outcome-row-summary';
import { Award01Icon, Coins01Icon, JusticeScale01Icon, Search02Icon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { formatPercent } from '@/lib/chart/format';
import { useFormattedCurrency } from '@/lib/currency';
import { useSimulatedTradeModalStore } from '@/stores/simulated-trade-modal';
interface MarketHowItWorksModalProps {
  marketPubkey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the "What is graduation?" sub-modal from an inline cross-link. */
  onWhatIsGraduation?: () => void;
  /** Opens the "Preventing manipulation" sub-modal from an inline cross-link. */
  onPreventManipulation?: () => void;
}

/**
 * "How this market works" — the per-market explainer opened from the `?` button
 * in the market detail header. Unlike the generic `MarketsHowItWorksModal`, this
 * one threads live data for THIS market (outcome count, current leader + its
 * share, the resolution threshold, and the prize pool) so the copy reads as a
 * narration of the market in front of the user rather than an abstract overview.
 *
 * All dynamic values are read through `useGetMarketField` field leaves so they
 * share the detail page's cache entry (no extra fetch) and stay live over NATS.
 */
export function MarketHowItWorksModal(t0) {
  const $ = _c(94);
  const {
    marketPubkey,
    open,
    onOpenChange,
    onWhatIsGraduation,
    onPreventManipulation
  } = t0;
  const t = useTranslations("market_how_it_works");
  const tLive = useTranslations("liveEvents.howItWorks");
  const {
    formatD6
  } = useFormattedCurrency();
  const openSimulatedTrade = useSimulatedTradeModalStore(_temp);
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = {
      pubkey: marketPubkey
    };
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const detailArgs = t1;
  const outcomes = useGetMarketField(detailArgs, "outcomes");
  const config = useMarketConfig(useGetMarketField(detailArgs, "marketConfig"));
  const timed = config?.resolutionMode === "timed";
  const thresholdD9 = config?.resolutionMode === "conviction" ? config.convictionResolutionThresholdPpb : null;
  const prizePoolD6 = useGetMarketField(detailArgs, "prizePool");
  let T0;
  let T1;
  let earnDescription;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== formatD6 || $[3] !== onOpenChange || $[4] !== open || $[5] !== openSimulatedTrade || $[6] !== outcomes || $[7] !== prizePoolD6 || $[8] !== t || $[9] !== tLive || $[10] !== thresholdD9 || $[11] !== timed) {
    const thresholdLabel = thresholdD9 == null ? null : formatPercent(convictionFraction(thresholdD9), 0);
    let hasPrizePool;
    let prizePoolValue;
    let t11;
    if ($[24] !== outcomes || $[25] !== prizePoolD6 || $[26] !== t) {
      const summaries = outcomes !== undefined ? buildOutcomeRowSummaries(outcomes) : [];
      const outcomeCount = summaries.length;
      const leader = summaries.toSorted(_temp2)[0];
      const hasLeader = leader !== undefined && leader.share > 0;
      prizePoolValue = String(prizePoolD6 ?? "0");
      hasPrizePool = Number(prizePoolValue) > 0;
      t11 = hasLeader ? t("question_description", {
        count: outcomeCount,
        leader: leader.label,
        leaderPct: formatPercent(leader.share, 1)
      }) : t("question_description_no_leader", {
        count: outcomeCount
      });
      $[24] = outcomes;
      $[25] = prizePoolD6;
      $[26] = t;
      $[27] = hasPrizePool;
      $[28] = prizePoolValue;
      $[29] = t11;
    } else {
      hasPrizePool = $[27];
      prizePoolValue = $[28];
      t11 = $[29];
    }
    const questionDescription = t11;
    let t12;
    if ($[30] !== formatD6 || $[31] !== hasPrizePool || $[32] !== prizePoolValue || $[33] !== t) {
      t12 = hasPrizePool ? t("earn_description", {
        prizePool: formatD6(prizePoolValue, "compact")
      }) : t("earn_description_no_pool");
      $[30] = formatD6;
      $[31] = hasPrizePool;
      $[32] = prizePoolValue;
      $[33] = t;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    earnDescription = t12;
    T1 = InfoModal;
    t5 = open;
    t6 = onOpenChange;
    t7 = "lg";
    if ($[35] !== t) {
      t8 = t("title");
      $[35] = t;
      $[36] = t8;
    } else {
      t8 = $[36];
    }
    t9 = timed ? tLive("description") : thresholdLabel === null ? undefined : t("description", {
      threshold: thresholdLabel
    });
    let t13;
    if ($[37] !== t) {
      t13 = t("got_it");
      $[37] = t;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    let t14;
    if ($[39] !== onOpenChange) {
      t14 = () => onOpenChange(false);
      $[39] = onOpenChange;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    let t15;
    if ($[41] !== onOpenChange || $[42] !== openSimulatedTrade) {
      t15 = <InteractiveExplainerButton onOpen={() => {
        onOpenChange(false);
        openSimulatedTrade(true);
      }} />;
      $[41] = onOpenChange;
      $[42] = openSimulatedTrade;
      $[43] = t15;
    } else {
      t15 = $[43];
    }
    if ($[44] !== t13 || $[45] !== t14 || $[46] !== t15) {
      t10 = <InfoModal.Footer cta={t13} onClose={t14} lead={t15} />;
      $[44] = t13;
      $[45] = t14;
      $[46] = t15;
      $[47] = t10;
    } else {
      t10 = $[47];
    }
    T0 = InfoModal.IconList;
    let t16;
    if ($[48] !== t) {
      t16 = t("question_title");
      $[48] = t;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    if ($[50] !== questionDescription || $[51] !== t16) {
      t2 = <InfoModal.IconItem icon={Search02Icon} title={t16} description={questionDescription} />;
      $[50] = questionDescription;
      $[51] = t16;
      $[52] = t2;
    } else {
      t2 = $[52];
    }
    let t17;
    if ($[53] !== t || $[54] !== tLive || $[55] !== timed) {
      t17 = timed ? tLive("duringTitle") : t("back_title");
      $[53] = t;
      $[54] = tLive;
      $[55] = timed;
      $[56] = t17;
    } else {
      t17 = $[56];
    }
    let t18;
    if ($[57] !== t || $[58] !== tLive || $[59] !== timed) {
      t18 = timed ? tLive("duringDescription") : t("back_description");
      $[57] = t;
      $[58] = tLive;
      $[59] = timed;
      $[60] = t18;
    } else {
      t18 = $[60];
    }
    if ($[61] !== t17 || $[62] !== t18) {
      t3 = <InfoModal.IconItem icon={Coins01Icon} title={t17} description={t18} />;
      $[61] = t17;
      $[62] = t18;
      $[63] = t3;
    } else {
      t3 = $[63];
    }
    t4 = timed || thresholdLabel !== null ? <InfoModal.IconItem icon={JusticeScale01Icon} title={timed ? tLive("endTitle") : t("resolve_title", {
      threshold: thresholdLabel ?? ""
    })} description={timed ? tLive("endDescription") : t("resolve_description", {
      threshold: thresholdLabel ?? ""
    })} /> : null;
    $[2] = formatD6;
    $[3] = onOpenChange;
    $[4] = open;
    $[5] = openSimulatedTrade;
    $[6] = outcomes;
    $[7] = prizePoolD6;
    $[8] = t;
    $[9] = tLive;
    $[10] = thresholdD9;
    $[11] = timed;
    $[12] = T0;
    $[13] = T1;
    $[14] = earnDescription;
    $[15] = t10;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[12];
    T1 = $[13];
    earnDescription = $[14];
    t10 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t11;
  if ($[64] !== t) {
    t11 = t("earn_title");
    $[64] = t;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== earnDescription || $[67] !== t11) {
    t12 = <InfoModal.IconItem icon={Award01Icon} title={t11} description={earnDescription} />;
    $[66] = earnDescription;
    $[67] = t11;
    $[68] = t12;
  } else {
    t12 = $[68];
  }
  let t13;
  if ($[69] !== T0 || $[70] !== t12 || $[71] !== t2 || $[72] !== t3 || $[73] !== t4) {
    t13 = <T0>{t2}{t3}{t4}{t12}</T0>;
    $[69] = T0;
    $[70] = t12;
    $[71] = t2;
    $[72] = t3;
    $[73] = t4;
    $[74] = t13;
  } else {
    t13 = $[74];
  }
  let t14;
  if ($[75] !== onWhatIsGraduation || $[76] !== t) {
    t14 = onWhatIsGraduation !== undefined ? [{
      key: "graduation",
      label: t("graduation_link"),
      onClick: onWhatIsGraduation
    }] : [];
    $[75] = onWhatIsGraduation;
    $[76] = t;
    $[77] = t14;
  } else {
    t14 = $[77];
  }
  let t15;
  if ($[78] !== onPreventManipulation || $[79] !== t) {
    t15 = onPreventManipulation !== undefined ? [{
      key: "manipulation",
      label: t("manipulation_link"),
      onClick: onPreventManipulation
    }] : [];
    $[78] = onPreventManipulation;
    $[79] = t;
    $[80] = t15;
  } else {
    t15 = $[80];
  }
  let t16;
  if ($[81] !== t14 || $[82] !== t15) {
    t16 = <DocsLinks docsPath="/concepts/opinion-markets" crossLinks={[...t14, ...t15]} />;
    $[81] = t14;
    $[82] = t15;
    $[83] = t16;
  } else {
    t16 = $[83];
  }
  let t17;
  if ($[84] !== T1 || $[85] !== t10 || $[86] !== t13 || $[87] !== t16 || $[88] !== t5 || $[89] !== t6 || $[90] !== t7 || $[91] !== t8 || $[92] !== t9) {
    t17 = <T1 open={t5} onOpenChange={t6} size={t7} title={t8} description={t9} footer={t10}>{t13}{t16}</T1>;
    $[84] = T1;
    $[85] = t10;
    $[86] = t13;
    $[87] = t16;
    $[88] = t5;
    $[89] = t6;
    $[90] = t7;
    $[91] = t8;
    $[92] = t9;
    $[93] = t17;
  } else {
    t17 = $[93];
  }
  return t17;
}
function _temp2(a, b) {
  return b.share - a.share;
}
function _temp(s) {
  return s.setOpen;
}